import axios from "axios";
import { FeatureCollection, BBox as IBBox } from "geojson";
import { LatLngExpression, LatLngLiteral } from "leaflet";
import { useMap } from "react-leaflet";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import {
  Attribute,
  Layer,
  MapPosition,
  MapPositionBounds,
  WMSFeatureInfoParams,
} from "../types";
import { parseToCollection, parseXML } from "../utils/XMLUtils";
import { bbox, ellipse } from "@turf/turf";

const initialPosition: MapPosition = {
  lat: 51.218157751766725,
  lng: 5.079049604319153,
  zoom: 18,
};

const VALID_XML_TYPES: DOMParserSupportedType[] = [
  "application/xhtml+xml",
  "application/xml",
  "image/svg+xml",
  "text/html",
  "text/xml",
];

export function useMapPosition(initial = initialPosition) {
  const [queryParams] = useSearchParams();

  return {
    ...initial,
    lat: parseFloat(queryParams.get("lat") || initial.lat.toString()),
    lng: parseFloat(queryParams.get("lon") || initial.lng.toString()),
    zoom: parseFloat(queryParams.get("zoom") || initial.zoom.toString()),
  };
}

export const useMapPoint = (): [
  LatLngLiteral | null,
  (point: LatLngLiteral | null) => void
] => {
  const [params, setParams] = useSearchParams();
  const rawPoint = params.get("point");
  const point = rawPoint
    ? arrayToLatLng(rawPoint.split(",").map(parseFloat))
    : null;

  function arrayToLatLng(pointArray: number[]): LatLngLiteral {
    const [lat, lng] = pointArray;
    return { lat, lng };
  }

  function updateParams(point: LatLngLiteral | null) {
    const newParams = new URLSearchParams(params);
    if (point) newParams.set("point", [point.lat, point.lng].join(","));
    else newParams.delete("point");

    setParams(newParams);
  }

  return [point, updateParams];
};

function useWMSFeatureInfoCoordinates(latlng: LatLngLiteral) {
  /**const bboxPrecision = 0.01;
  const bbox = [
    latlng.lng - bboxPrecision,
    latlng.lat - bboxPrecision,
    latlng.lng + bboxPrecision,
    latlng.lat + bboxPrecision,
  ].join(",");

  return {
    x: 25,
    y: 25,
    width: 50,
    height: 50,
    bbox,
  };*/
  const { zoom } = useMapPosition();
  const bboxPrecision = {
    18: 0.01,
    17: 0.01,
    16: 0.01,
    15: 0.05,
    14: 0.05,
    13: 0.1,
    12: 0.15,
    11: 0.4,
    10: 0.7,
    9: 1.5,
    8: 2.8,
    7: 2.8,
    6: 10,
    5: 11,
    4: 50,
    3: 60,
    2: 80,
    1: 100,
  };
  type BoxPrecisionKey = keyof typeof bboxPrecision;
  // const bboxPrecision = 0.01 * (18 / zoom) || 1;
  const point = [latlng?.lat || 0, latlng?.lng || 0];

  const computedEllipse = ellipse(
    point,
    bboxPrecision[zoom as BoxPrecisionKey],
    bboxPrecision[zoom as BoxPrecisionKey]
  );
  const computedBBox: IBBox = bbox(computedEllipse);

  return {
    x: 25,
    y: 25,
    width: 50,
    height: 50,
    bbox: `${computedBBox[1]},${computedBBox[0]},${computedBBox[3]},${computedBBox[2]}`,
  };
}

export const useDefaultFeature = (
  layer: Layer,
  mapPosition: MapPositionBounds,
  layers?: string,
  groupingKey?: string,
  groupingCount: number = 50,
  attributes: Attribute[] = []
) => {
  const pointParams = useWMSFeatureInfoCoordinates(mapPosition.position);
  async function fetch() {
    const config: {
      params: WMSFeatureInfoParams;
    } = {
      params: {
        version: "1.1.1",
        request: "GetFeatureInfo",
        service: "WMS",
        srs: "EPSG:4326",
        layers: layers || layer.wms.layers || "",
        query_layers: layers || layer.wms.layers,
        styles: layer.wms.styles || "",
        ...pointParams,
        info_format: "application/json",
      },
    };
    if (groupingKey) {
      const mappedAttr = attributes
        .filter((attr) => attr.grouping)
        .flatMap((attr) => [attr.label, attr.key]);
      //config.params.styles = "";
      config.params.feature_count = groupingCount;
      config.params.propertyName = `${
        mappedAttr.length > 0 ? `${mappedAttr.join(",")},` : ""
      }${groupingKey}`;
    }

    const response = await axios.get<FeatureCollection | string>(
      layer.wms.url,
      config
    );
    const { data, headers } = response;
    const contentType = VALID_XML_TYPES.find(
      (type) => type === headers["content-type"]
    );
    // TODO: refactor data parsing from data fetch
    if (
      typeof data === "string" &&
      headers &&
      "content-type" in headers &&
      (contentType ||
        headers["content-type"] === "application/vnd.ogc.se_xml;charset=UTF-8")
    ) {
      const xml = parseXML(data, contentType);
      if (xml) return parseToCollection(xml, pointParams.x, pointParams.y);
    }

    if (typeof data === "string") {
      return Promise.reject(`Invalid data:\n${data}`);
    }

    return data;
  }

  return useQuery(
    ["feature", layer.id, layers, mapPosition.position, groupingKey],
    fetch,
    {
      retry: false,
      onError: (error) => console.log("useDefaultFeature() onError:", error),
    }
  );
};

export const useFlandersFeature = (
  layer: Layer,
  mapPosition: MapPositionBounds,
  layers?: string,
  groupingKey?: string,
  groupingCount: number = 50
) => {
  const pointParams = useWMSFeatureInfoCoordinates(mapPosition.position);
  async function fetch() {
    const featureInfo = await getFeatureInfo(layer, mapPosition);

    if (featureInfo === undefined) {
      return;
    }

    //TODO: fix vlaanderen WMS stuff! need extra CQL_filter property!
    // const filterValue = featureInfo.properties![layer.wms.cqlFilter || ""];
    const filterValue = ""; //TODO: remove later, see line above
    return getFeature(layer, filterValue);
  }

  async function getFeatureInfo(
    layer: Layer,
    event: MapPositionBounds
  ): Promise<FeatureCollection | undefined> {
    const config = {
      params: {
        version: "1.1.0",
        request: "GetFeatureInfo",
        service: "WMS",
        srs: "EPSG:4326",
        layers: layer.wms.layers,
        query_layers: layer.wms.layers,
        ...pointParams,
      },
    };

    try {
      const response = await axios.get<string>(layer.wms.url, config);
      const { data, headers } = response;
      const contentType = VALID_XML_TYPES.find(
        (type) => type === headers["content-type"]
      );
      if (
        typeof data === "string" &&
        headers &&
        "content-type" in headers &&
        contentType
      ) {
        const xml = parseXML(data, contentType);
        if (xml) return parseToCollection(xml, pointParams.x, pointParams.y);
      }
      throw Error("Oops, could not fetch the data!");

      //TODO: orginal code.. check if the code above does the same
      // const converted: any = convertXmlJs.xml2js(data, { compact: true });
      // var singleobject = {
      //   ...converted.FeatureInfoResponse.FIELDS._attributes,
      //   lat: event.position.lat,
      //   lng: event.position.lng,
      // };
      // return GeoJSON.parse(singleobject, { Point: ["lat", "lng"] });
    } catch (ex) {
      throw ex;
    }
  }

  async function getFeature(
    layer: Layer,
    cqlValue: string
  ): Promise<FeatureCollection | undefined> {
    // if (layer.wfs === undefined) {
    //   console.warn("Can't perform request GetFeature without wfs config");
    //   return;
    // }
    const config = {
      params: {
        version: "1.0.0", //TODO: check if 1.0.0 is correct and should not be 1.1.1
        request: "GetFeature",
        service: "WFS",
        outputFormat: "application/json",
        srsName: "EPSG:4326",
        // typename: layer.wfs.typename,
        // CQL_FILTER: layer.wfs.cqlFilter + "=" + cqlValue,
      },
    };

    try {
      const { data } = await axios.get<FeatureCollection>(
        layer.wms.url,
        config
      );
      return data;
    } catch (ex) {
      console.error(ex);
    }
  }

  return useQuery(["feature", layer.id, mapPosition.position], fetch, {
    retry: false,
  });
};

export const useMapPositionBounds = (
  latlng: LatLngExpression
): Pick<WMSFeatureInfoParams, "x" | "y" | "width" | "height" | "bbox"> => {
  const map = useMap();
  const containerPoint = map.latLngToContainerPoint(latlng);
  const size = map.getSize();
  const bbox = map.getBounds().toBBoxString();

  return { width: size.x, height: size.y, bbox, ...containerPoint };
};
